/* eslint-disable react/forbid-prop-types */
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

function DashboardFilter({
  showFilterDialog,
  onHide,
  filters,
  setFilters,
  setAppliedFilters,
  organizations,
  programs,
  isSelectOne,
  localStorageKey,
}) {
  const { t } = useTranslation();

  const yearProposeds = Array.from({ length: 8 }, (_, i) => ({
    year: new Date().getFullYear() - i,
  }));

  const handleOnChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
  };
  // Filter components
  const yearProposedFilter = () => (
    <div className="flex flex-column gap-2">
      <label className="font-semibold">{t('dashboard.filter.label.yearProposed')}</label>
      {isSelectOne?.yearProposed ? (
        <Dropdown
          value={filters.yearProposed}
          onChange={(e) => {
            handleOnChange('yearProposed', e.value);
          }}
          options={yearProposeds}
          optionLabel="year"
          optionValue="year"
          showClear
          placeholder={t('dashboard.filter.placeholder.yearProposed')}
          className="w-full"
        />
      ) : (
        <MultiSelect
          value={
            Array.isArray(filters.yearProposed) ? filters.yearProposed : [filters.yearProposed]
          }
          onChange={(e) => {
            handleOnChange('yearProposed', e.value);
          }}
          options={yearProposeds}
          optionLabel="year"
          optionValue="year"
          filter
          showClear
          placeholder={t('dashboard.filter.placeholder.yearProposed')}
          maxSelectedLabels={3}
          className="w-full"
        />
      )}
    </div>
  );

  const organizationFilter = () => (
    <div className="flex flex-column gap-2">
      <label className="font-semibold">{t('dashboard.filter.label.organization')}</label>
      {isSelectOne?.organizationId ? (
        <Dropdown
          value={filters.organizationId}
          onChange={(e) => {
            handleOnChange('organizationId', e.value);
          }}
          options={organizations}
          optionLabel="name"
          optionValue="id"
          showClear
          placeholder={t('dashboard.filter.placeholder.organization')}
          className="w-full"
        />
      ) : (
        <MultiSelect
          value={
            Array.isArray(filters.organizationId)
              ? filters.organizationId
              : [filters.organizationId]
          }
          onChange={(e) => {
            handleOnChange('organizationId', e.value);
          }}
          options={organizations}
          optionLabel="name"
          optionValue="id"
          filter
          showClear
          placeholder={t('dashboard.filter.placeholder.organization')}
          maxSelectedLabels={3}
          className="w-full"
        />
      )}
    </div>
  );

  const programFilter = () => (
    <div className="flex flex-column gap-2">
      <label className="font-semibold">{t('dashboard.filter.label.program')}</label>
      {isSelectOne?.programId ? (
        <Dropdown
          value={filters.programId}
          onChange={(e) => {
            handleOnChange('programId', e.value);
          }}
          options={programs}
          optionLabel="name"
          optionValue="id"
          showClear
          placeholder={t('dashboard.filter.placeholder.program')}
          className="w-full"
        />
      ) : (
        <MultiSelect
          value={Array.isArray(filters.programId) ? filters.programId : [filters.programId]}
          onChange={(e) => {
            handleOnChange('programId', e.value);
          }}
          options={programs}
          optionLabel="name"
          optionValue="id"
          filter
          showClear
          placeholder={t('dashboard.filter.placeholder.program')}
          maxSelectedLabels={3}
          className="w-full"
        />
      )}
    </div>
  );

  const handleClearFilters = () => {
    setFilters({
      yearProposed: isSelectOne?.yearProposed ? null : [],
      organizationId: isSelectOne?.organizationId ? null : [],
      programId: isSelectOne?.programId ? null : [],
    });
    setAppliedFilters({
      yearProposed: isSelectOne?.yearProposed ? null : [],
      organizationId: isSelectOne?.organizationId ? null : [],
      programId: isSelectOne?.programId ? null : [],
    });
    localStorage.removeItem(localStorageKey);
    onHide();
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    localStorage.setItem(localStorageKey, JSON.stringify(filters));
    onHide();
  };

  const filterDialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={t('dashboard.filter.clear')}
        severity="secondary"
        onClick={handleClearFilters}
        className="p-button-outlined"
      />
      <Button label={t('dashboard.filter.apply')} severity="success" onClick={handleApplyFilters} />
    </div>
  );

  return (
    <Dialog
      header={t('dashboard.filter.label.title')}
      visible={showFilterDialog}
      onHide={onHide}
      footer={filterDialogFooter}
      style={{ minWidth: '340px', minHeight: '400px', width: '40vw' }}
      breakpoints={{ '960px': '80vw', '641px': '95vw' }}
      draggable={false}
    >
      <div className="flex flex-column gap-4">
        {yearProposedFilter()}
        {organizationFilter()}
        {programFilter()}
      </div>
    </Dialog>
  );
}

DashboardFilter.propTypes = {
  showFilterDialog: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  setAppliedFilters: PropTypes.func.isRequired,
  organizations: PropTypes.array.isRequired,
  programs: PropTypes.array.isRequired,
  isSelectOne: PropTypes.shape({
    yearProposed: PropTypes.bool,
    organizationId: PropTypes.bool,
    programId: PropTypes.bool,
  }).isRequired,
  localStorageKey: PropTypes.string.isRequired,
};

export default DashboardFilter;
